import React, { useEffect, useReducer, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { initialState, reducer } from 'features/automaticStateTransition/reducers/stateTransitions/containerReducer';
import { useValidation } from '@totalsoft/pure-validations-react';
import { automaticStateTransitionRuleValidator } from 'features/automaticStateTransition/validators';
import StateTransitionListCard from './StateTransitionListCard';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { DOCUMENT_TYPE_WITH_ALLOWED_STATES } from '../../../queries';
import { AUTOMATICSTATETRANSITIONRULE_SAVE, AUTOMATICSTATETRANSITIONRULE_DELETE } from '../../../mutations';
import { AUTOMATICSTATETRANSITIONRULE_SAVE_SUBSCRIPTION, AUTOMATICSTATETRANSITIONRULE_DELETE_SUBSCRIPTION } from '../../../subscriptions';
import { useToast } from 'hooks/toasts/useToast';

const StateTransitionListContainer = ({ automaticStateTransitionId, documentTypeId, data }) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);

  const [automaticStateTransitionRuleValidaton, validateAutomaticStateTransitionRule] = useValidation(
    automaticStateTransitionRuleValidator
  );

  // #region Queries

  const { data: documentTypeWithAllowedStates } = useQuery(DOCUMENT_TYPE_WITH_ALLOWED_STATES, {
    variables: {
      documentTypeWithAllowedStatesId: documentTypeId
    },
    skip: !documentTypeId
  });

  const states = useMemo(
    () => documentTypeWithAllowedStates?.documentTypeWithAllowedStates.allowedStates || [],
    [documentTypeWithAllowedStates]
  );

  // #endregion

  // #region Mutations

  const [AutomaticStateTransitionRuleSave] = useMutation(AUTOMATICSTATETRANSITIONRULE_SAVE);

  const [AutomaticStateTransitionRuleDelete] = useMutation(AUTOMATICSTATETRANSITIONRULE_DELETE);

  // #endregion

  // #region Subscriptions

  useSubscription(AUTOMATICSTATETRANSITIONRULE_SAVE_SUBSCRIPTION, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AutomaticStateTransitions.AutomaticStateTransitionRuleSaved'), 'success');
      dispatch({
        type: 'OnRuleSaved',
        newData: data?.automaticStateTransitionRuleUpdated?.automaticStateTransitionRule
      });
      client.cache.evict({
        id: `AutomaticStateTransition:{"automaticStateTransitionId":${data?.automaticStateTransitionRuleUpdated?.automaticStateTransitionRule?.automaticStateTransitionId}}`
      });
      client.cache.gc();
    }
  });

  useSubscription(AUTOMATICSTATETRANSITIONRULE_DELETE_SUBSCRIPTION, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AutomaticStateTransitions.AutomaticStateTransitionRuleRemoved'), 'success');
      client.cache.evict({
        id: `TransitionRule:{"automaticStateTransitionRuleId":${data?.automaticStateTransitionRuleDeleted?.automaticStateTransitionRuleId}}`
      });
    }
  });

  // #endregion

  // #region Handlers

  const handleAdd = () => {
    dispatch({ type: 'OnAddRule' });
  };

  const handleDelete = index => () => {
    const input = { automaticStateTransitionRuleId: localState.details[index].automaticStateTransitionRuleId };
    if (window.confirm(t('AutomaticStateTransitionRules.DeleteRule'))) {
      if (input.automaticStateTransitionRuleId !== null) {
        AutomaticStateTransitionRuleDelete({ variables: { input: input } });
      }
      dispatch({ type: 'OnDeleteRule', index });
    }
  };

  const handleSave = idx => () => {
    // eslint-disable-next-line no-unused-vars
    const { changed, toStateName, isExpanded, ...rule } = localState.details[idx];

    return AutomaticStateTransitionRuleSave({ variables: { input: { ...rule } } });
  };

  const handlePropertyChanged = (propertyId, propertyIdValue, index) => {
    let payload;
    switch (propertyId) {
      case 'toStateId': {
        let detail = states.find(x => x.documentStateId === propertyIdValue);
        payload = {
          propertyId: propertyId,
          propertyIdValue: propertyIdValue,
          propertyName: 'toStateName',
          propertyNameValue: detail != null ? detail.documentStateName : '',
          index
        };
        break;
      }
      default:
        payload = { propertyId: propertyId, propertyIdValue, index };
        break;
    }

    dispatch({ type: 'OnDetailsPropertyChanged', payload });
  };

  const handleExpandChange = (isExpanded, index) => {
    dispatch({ type: 'OnRuleExpanded', index, isExpanded });
  };

  // #endregion

  // #region Side effects

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data: { data, automaticStateTransitionId, states } });
  }, [automaticStateTransitionId, data, states]);

  useEffect(() => {
    validateAutomaticStateTransitionRule(localState.details);
  }, [localState.details, validateAutomaticStateTransitionRule]);

  // #endregion

  return (
    <StateTransitionListCard
      automaticStateTransitionRules={localState.details}
      states={states}
      onSave={handleSave}
      onAdd={handleAdd}
      onPropertyChanged={handlePropertyChanged}
      onDelete={handleDelete}
      onExpandChange={handleExpandChange}
      validation={automaticStateTransitionRuleValidaton}
    />
  );
};

StateTransitionListContainer.propTypes = {
  automaticStateTransitionId: PropTypes.number.isRequired,
  documentTypeId: PropTypes.number,
  data: PropTypes.array.isRequired
};

export default StateTransitionListContainer;
